"use client";

import { useEffect, useRef, useState } from "react";
import HouseCompetitionForDesktop from "./houseCompetitionForDesktop";
import HouseCompetitionForMobile from "./houseCompetitionForMobile";
import { getCompetitionEndDate, getHouseAmeneties, getHousePrizeDetails } from "./house-service";
import { CountdownTimerData, HousePrizeBasicDetailsData, HouseContextDetails, HouseAmenetiesData } from "./types";
import CountdownTimer from "../../../components/Countdown/countdown";
import "./houseCompetition.css";
import { HouseCompetitionData } from "./contextProvider";
import "react-loading-skeleton/dist/skeleton.css";
import Modal from "../../../components/Modal/modal";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { publishModalVisibility } from "../../../redux/features/modal/modalSlice";
import { DEVICE_RESOLUTION } from "../../../app-constants";
import { ToastProvider } from "../../../components/ToastNotification/toastProvider";
import { getCurrencies } from "../../../common-service";
import { AppCurrencies } from "../../../common";
import BotbPassDrawer from "../../botb-pass-drawer/BotbPassDrawer";

const HouseCompetition = () => {
  const [windowInnerWidth, setWindowInnerWidth] = useState<number>(window.innerWidth);
  const [contextData, setContextData] = useState<HouseContextDetails>();
  const [competitionEndDate, setCompetitionEndDate] = useState<Date>();
  const isModalOpen = useAppSelector(state => state.modal.isOpen);
  const modalData = useAppSelector(state => state.modal.data);
  const modalOverlay = useAppSelector(state => state.modal.withOverlay);
  const modalClassNames = useAppSelector(state => state.modal.modalClassNames);
  const modalStyles = useAppSelector(state => state.modal.modalStyles);
  const dispatch = useAppDispatch();
  const selectedCurrencyCode = useRef("GBP");

  useEffect(() => {
    getCurrencyList();
    getCountdownTimerData();
    renderRespectiveLayout();
    window.addEventListener("resize", renderRespectiveLayout);
    return () => window.removeEventListener("resize", renderRespectiveLayout);
  }, []);

  const getCurrencyList = async () => {
    try {
      const response = await getCurrencies();
      const data = (await response.json()) as AppCurrencies;
      selectedCurrencyCode.current = data.selectedCurrency?.CurrencyCode || "GBP";
    } catch (err) {
      console.error(`Error while retrieving currencies - ${err}`);
    } finally {
      getCompetitionContextDetails();
    }
  };

  const getCompetitionContextDetails = () => {
    let compId, prizeId, prizeTitle;
    const houseCompIdSpanElem = document.querySelector(".house-comp-id") as HTMLSpanElement;
    if (houseCompIdSpanElem) {
      compId = parseInt(houseCompIdSpanElem.textContent, 10);
    }
    const housePrizeIdSpanElem = document.querySelector(".house-prize-id") as HTMLSpanElement;
    if (housePrizeIdSpanElem) {
      prizeId = parseInt(housePrizeIdSpanElem.textContent, 10);
    }
    const housePrizeTitleSpanElem = document.querySelector(".house-prize-title") as HTMLSpanElement;
    if (housePrizeTitleSpanElem) {
      prizeTitle = housePrizeTitleSpanElem.textContent;
    }
    getPrizeBasicDetails(prizeId, compId, prizeTitle);
  };

  const getPrizeBasicDetails = async (prizeId: number, compId: number, prizeTitle: string) => {
    const [prizeBasicDataResponse, amenitiesDataResponse] = await Promise.all([
      getHousePrizeDetails(prizeId, selectedCurrencyCode.current),
      getHouseAmeneties(prizeId),
    ]);
    const prizeData = (await prizeBasicDataResponse.json()) as HousePrizeBasicDetailsData;
    const amentiesData = (await amenitiesDataResponse.json()) as HouseAmenetiesData;
    const shouldShowSubscriptionDrawer = prizeData.ShouldShowSubscriptionDrawer;
    setContextData({
      competitionId: compId,
      prizeId: prizeId,
      basePriceWithoutCurrency: prizeData?.TicketBasePriceWithoutCurrency,
      currencySymbol: prizeData?.CurrencySymbol,
      cashAlternativePopupText: prizeData?.HouseCashAlternativeData,
      cashAlternativeText: prizeData?.HouseCashAlternativeText,
      detailsText: prizeData?.DetailsText,
      prizeSubtitle: prizeData?.HousePrizeSubtitle,
      activePriceWithoutCurrency: prizeData?.TicketActivePriceWithoutCurrency,
      amenitiesPopupText: amentiesData?.HouseAmenities?.PopupText,
      amentiesImages: amentiesData?.HouseAmenities?.AmenityImages,
      prizeTitle: prizeTitle,
      isPrizeAvailable: prizeData?.PrizeAvailable,
      discountPercentage: prizeData?.DiscountPercentValue,
      overrideDiscountValue: Number(prizeData?.OverrideDiscountPercentValue),
      basePriceWithCurrency: prizeData?.TicketBasePriceWithCurrency,
      socialReviews: prizeData?.OverlayContent,
      prizeTags: prizeData?.PrizeTags,
      shouldShowSubscriptionDrawer: shouldShowSubscriptionDrawer,
      hideCashAlternative: prizeData?.HideCashAlternative,
      isHousePrizeOnly: prizeData?.IsHousePrizeOnly,
    });
  };

  const renderRespectiveLayout = () => {
    setWindowInnerWidth(window.innerWidth);
  };

  const getCountdownTimerData = async () => {
    const response = await getCompetitionEndDate();
    const { CompetitionEndDate } = (await response.json()) as CountdownTimerData;
    setCompetitionEndDate(new Date(CompetitionEndDate));
  };

  return (
    <HouseCompetitionData.Provider value={{ ...contextData }}>
      <CountdownTimer
        EndDateTime={competitionEndDate}
        textClass={`text-[#BCBCC0] font-normal text-[16px]`}
        numericClass="text-[#FF8200] font-medium text-[16px] self-end"
        containerClass="flex justify-center bg-[#000] p-3 gap-1"
        format="d:h:m:s"
      />
      <article className="mx-auto mainSection my-5">
        {windowInnerWidth >= DEVICE_RESOLUTION.Desktop ? <HouseCompetitionForDesktop /> : <HouseCompetitionForMobile />}
      </article>
      {contextData && <BotbPassDrawer shouldShowSubscriptionDrawer={contextData.shouldShowSubscriptionDrawer} />}
      <Modal
        withOverlay={modalOverlay}
        closeModal={event => {
          event.stopPropagation();
          dispatch(publishModalVisibility({ isOpen: false }));
        }}
        isOpen={isModalOpen}
        body={modalData}
        title=""
        modalClassNames={modalClassNames}
        customStyles={modalStyles}
      />
      <ToastProvider />
    </HouseCompetitionData.Provider>
  );
};

export default HouseCompetition;
